import { useRef, useState, type PointerEvent as RPointerEvent } from 'react';
import { AudioBus } from '@goodtime/engine';
import { submitWheelCommand } from '../../api/wheelCommand';
import { GESTURES, GESTURE_BY_ID, RECIPROCATE_GESTURE_ID } from './gestures';
import { rememberSentGesture } from './gestureMemory';

/** Hold this long to expand the full gesture set instead of firing the
 *  default high-five on release. */
const LONG_PRESS_MS = 300;

export interface SeatGestureButtonProps {
  /** Seat the gesture is sent to. */
  targetSeatId: number;
  roomId: string | null;
  playerId: string | null;
  /** Button diameter in px. Default 26. */
  size?: number;
}

/**
 * Quick gesture affordance pinned to the bottom-left of a seat avatar
 * (2026-06-08 producer brief: "opening the menu is slow"). A tap fires a
 * high-five at the seat immediately; a long-press expands the full
 * gesture set (good-luck / cheers) to the right. The directed gesture
 * drives the recipient's GestureToast + a brief float over their avatar,
 * and a bot replies once.
 *
 * <p>Self-contained — needs only the room + local player id to POST the
 * REACTION. Renders null for audience viewers (no playerId). The caller
 * pins it over a {@code position: relative} avatar container and omits it
 * for the local player's own seat + EMPTY seats.
 */
export function SeatGestureButton({
  targetSeatId,
  roomId,
  playerId,
  size = 26,
}: SeatGestureButtonProps) {
  const [expanded, setExpanded] = useState(false);
  const timerRef = useRef<number | null>(null);
  const longPressedRef = useRef(false);

  if (roomId === null || playerId === null) return null;

  const send = (gestureId: string): void => {
    AudioBus.shared.play('ui:click');
    // Remember we initiated this gesture at this seat, so when they
    // gesture back we render "...back" with no reciprocate button.
    rememberSentGesture(targetSeatId, gestureId);
    void submitWheelCommand(roomId, 'REACTION', playerId, {
      reactionId: gestureId,
      targetSeatId,
    }).catch(() => {
      /* fire-and-forget — a dropped gesture is invisible */
    });
    setExpanded(false);
  };

  const clearTimer = (): void => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const onPointerDown = (e: RPointerEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    longPressedRef.current = false;
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      longPressedRef.current = true;
      setExpanded(true);
    }, LONG_PRESS_MS);
  };

  const onPointerUp = (e: RPointerEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    clearTimer();
    // Tap (not a long-press) → default high-five.
    if (!longPressedRef.current && !expanded) {
      send(RECIPROCATE_GESTURE_ID);
    }
  };

  const inner = Math.round(size * 0.72);
  const highFive = GESTURE_BY_ID[RECIPROCATE_GESTURE_ID];

  return (
    <div
      style={{
        position: 'absolute',
        bottom: -3,
        zIndex: 14,
        pointerEvents: 'auto',
        // Collapsed: pinned to the avatar's bottom-left corner. Expanded:
        // centred on the avatar so the gesture row grows out both ways and
        // never clips past the avatar's right edge.
        ...(expanded
          ? { left: '50%', transform: 'translateX(-50%)' }
          : { left: -3 }),
      }}
      // Don't let a corner tap also open the avatar's ProfileCard.
      onClick={(e) => e.stopPropagation()}
    >
      {expanded ? (
        <div
          onPointerLeave={() => setExpanded(false)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            padding: 4,
            borderRadius: 999,
            background: 'rgba(11,24,56,0.94)',
            border: '1.5px solid rgba(168,224,255,0.85)',
            boxShadow: '0 3px 12px rgba(0,0,0,0.5)',
          }}
        >
          {GESTURES.map((g) => (
            <button
              key={g.id}
              type="button"
              aria-label={g.label}
              onClick={(e) => {
                e.stopPropagation();
                send(g.id);
              }}
              style={{
                width: size,
                height: size,
                borderRadius: '50%',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                background: 'rgba(255,255,255,0.10)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src={g.src}
                alt=""
                width={inner}
                height={inner}
                draggable={false}
                style={{ display: 'block' }}
              />
            </button>
          ))}
        </div>
      ) : (
        <button
          type="button"
          aria-label="High five (long-press for more gestures)"
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onPointerLeave={clearTimer}
          onPointerCancel={clearTimer}
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            border: '1.5px solid rgba(168,224,255,0.8)',
            padding: 0,
            cursor: 'pointer',
            background: 'rgba(11,24,56,0.85)',
            boxShadow: '0 2px 6px rgba(0,0,0,0.45)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: 'none',
          }}
        >
          <img
            src={highFive?.src}
            alt=""
            width={inner}
            height={inner}
            draggable={false}
            style={{ display: 'block', pointerEvents: 'none' }}
          />
        </button>
      )}
    </div>
  );
}
